import React, { useState, useEffect } from 'react';
import { 
  PrinterIcon, 
  DocumentArrowDownIcon, 
  EnvelopeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { RefoulementTicketData } from '@/types/refoulementTicket';
import refoulementTicketService from '@/services/refoulementTicketService';
import QRCode from 'qrcode';

interface RefoulementTicketProps {
  ticketData: RefoulementTicketData;
  onClose?: () => void;
  onPrint?: () => void;
  onExport?: () => void;
  onEmail?: () => void;
  showActions?: boolean;
  className?: string;
  isModal?: boolean; // Si true, affiche comme une modale
}

const RefoulementTicket: React.FC<RefoulementTicketProps> = ({
  ticketData,
  onClose,
  onPrint,
  onExport,
  onEmail,
  showActions = true,
  className = '',
  isModal = false
}) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Initialisation du composant
  React.useEffect(() => {
    // Composant monté
  }, [ticketData]);

  // Générer le QR code
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const qrData = {
          type: 'refoulement',
          numero: ticketData.numero_refoulement,
          date: ticketData.date_refoulement,
          pesee: ticketData.pesee.reference || ticketData.pesee.code,
          agent: ticketData.agent_refoulement
        };

        const qrString = JSON.stringify(qrData);
        const dataURL = await QRCode.toDataURL(qrString, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        
        setQrCodeDataURL(dataURL);
      } catch (error) {
        console.error('Erreur lors de la génération du QR code:', error);
      }
    };

    generateQRCode();
  }, [ticketData]);

  const formatWeight = (weight: number) => `${weight.toLocaleString()} kg`;
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('fr-FR');
  const formatDateTime = (dateString: string) => new Date(dateString).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const handlePrint = async () => {
    if (onPrint) {
      onPrint();
      return;
    }

    setIsPrinting(true);
    try {
      await refoulementTicketService.printTicket(ticketData, {
        onSuccess: () => {
          setIsPrinting(false);
        },
        onError: (err: string) => {
          console.error('Erreur lors de l\'impression:', err);
          setIsPrinting(false);
        }
      });
    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      setIsPrinting(false);
    }
  };

  const handleExport = async () => {
    if (onExport) {
      onExport();
      return;
    }

    setIsExporting(true);
    try {
      await refoulementTicketService.exportTicketPDF(ticketData, {
        onSuccess: () => {
          setIsExporting(false);
        },
        onError: (err: string) => {
          console.error('Erreur lors de l\'export:', err);
          setIsExporting(false);
        }
      });
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      setIsExporting(false);
    }
  };

  const handleEmail = async () => {
    if (onEmail) {
      onEmail();
      return;
    }

    setIsSendingEmail(true);
    try {
      await refoulementTicketService.sendTicketEmail(
        ticketData,
        '',
        undefined,
        undefined,
        undefined,
        undefined,
        {
          onSuccess: () => {
            setIsSendingEmail(false);
          },
          onError: (err: string) => {
            console.error('Erreur lors de l\'envoi par email:', err);
            setIsSendingEmail(false);
          }
        }
      );
    } catch (error) {
      console.error('Erreur lors de l\'envoi par email:', error);
      setIsSendingEmail(false);
    }
  };

  const ticketContent = (
    <div className={`bg-white rounded-xl shadow-lg py-6 px-10 ${className}`} id="refoulement-ticket-content">
      {/* En-tête du ticket */}
      <div className="text-center border-b-2 border-red-300 pb-4 mb-6">
        {/* Logo et organisation */}
        <div className="flex justify-between items-start mb-4">
          <div className="text-left">
            <div className="flex items-center mb-2">
              <img 
                src="/images/cca-logo.svg" 
                alt="Logo CCA" 
                className="h-8 w-auto mr-3"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                  if (nextElement) {
                    nextElement.style.display = 'flex';
                  }
                }}
              />
              <div className="w-8 h-8 bg-green-600 rounded-full mr-2 flex items-center justify-center" style={{display: 'none'}}>
                <span className="text-white text-xs font-bold">CA</span>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">Le Conseil du Coton et de l'Anacarde</h3>
                <p className="text-xs text-gray-600">Organisation de régulation</p>
              </div>
            </div>
          </div>
          
          {/* QR Code */}
          <div className="text-center">
            {qrCodeDataURL && (
              <div className="mb-2">
                <img 
                  src={qrCodeDataURL} 
                  alt="QR Code" 
                  className="w-20 h-20 mx-auto border border-gray-300 rounded"
                />
                <p className="text-xs text-gray-600 mt-1">QR Code</p>
              </div>
            )}
          </div>
          
          {/* Logo Pontcita */}
          <div className="text-right">
            <img 
              src="/images/logo-pontcita.png" 
              alt="Logo Pontcita" 
              className="h-12 w-auto mx-auto"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                if (nextElement) {
                  nextElement.style.display = 'flex';
                }
              }}
            />
            <div className="w-12 h-12 bg-orange-600 rounded-full mx-auto flex items-center justify-center" style={{display: 'none'}}>
              <span className="text-white text-xs font-bold">PC</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">Pontcita</p>
          </div>
        </div>
        
        {/* Informations de date et refoulement */}
        <div className="text-center text-sm mb-4">
          <div className="space-y-1">
            <div><span className="text-red-600 font-medium">Date refoulement:</span> {formatDate(ticketData.date_refoulement)}</div>
            <div><span className="text-red-600 font-medium">Pesée:</span> {ticketData.pesee.reference || ticketData.pesee.code}</div>
            <div><span className="text-red-600 font-medium">Campagne:</span> {ticketData.campaignYear || new Date().getFullYear()}</div>
            <div><span className="text-red-600 font-medium">Agent:</span> {ticketData.agent_refoulement_nom || ticketData.agent_refoulement_username || ticketData.agent_refoulement}</div>
          </div>
        </div>
        
        <h1 className="text-3xl font-bold text-red-600 mb-2">TICKET DE REFOULEMENT</h1>
        <div className="text-lg font-semibold text-gray-700 mb-2">
          <span className="mr-4">PESEE</span>
          <span>REFOULEE</span>
        </div>
        
        <div className="text-2xl font-mono text-red-600 font-bold">
          {ticketData.numero_refoulement}
        </div>
      </div>

      {/* Informations de la pesée refoulée */}
      <div className="mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-300 pb-1">Informations de la pesée</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600 font-medium">Référence:</span>
              <span className="text-red-600 font-medium">{ticketData.pesee.reference || ticketData.pesee.code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Véhicule:</span>
              <span className="font-medium">{ticketData.pesee.vehicle}</span>
            </div>
            {ticketData.pesee.trailerRegistration && (
              <div className="flex justify-between">
                <span className="text-gray-600">Remorque:</span>
                <span className="font-medium">{ticketData.pesee.trailerRegistration}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Chauffeur:</span>
              <span className="font-medium">
                {ticketData.pesee.driver}
                {ticketData.pesee.chauffeurCode && `/${ticketData.pesee.chauffeurCode}`}
              </span>
            </div>
            {ticketData.pesee.transporterName && (
              <div className="flex justify-between">
                <span className="text-gray-600">Transporteur:</span>
                <span className="font-medium">{ticketData.pesee.transporterName}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Produit:</span>
              <span className="font-medium">{ticketData.pesee.product}</span>
            </div>
            {ticketData.pesee.provenance && (
              <div className="flex justify-between">
                <span className="text-gray-600">Provenance:</span>
                <span className="font-medium">{ticketData.pesee.provenance}</span>
              </div>
            )}
            {ticketData.pesee.destination && (
              <div className="flex justify-between">
                <span className="text-gray-600">Destination:</span>
                <span className="font-medium">{ticketData.pesee.destination}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Poids brut:</span>
              <span className="text-red-600 font-medium">{formatWeight(ticketData.pesee.grossWeight)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Poids tare:</span>
              <span className="font-medium">{formatWeight(ticketData.pesee.tareWeight)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Poids net:</span>
              <span className="text-red-600 font-bold text-lg">{formatWeight(ticketData.pesee.netWeight)}</span>
            </div>
            {ticketData.pesee.bagWeight && (
              <div className="flex justify-between">
                <span className="text-gray-600">Poids sacs:</span>
                <span className="font-medium">{formatWeight(ticketData.pesee.bagWeight)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Site:</span>
              <span className="font-medium">{ticketData.pesee.site}</span>
            </div>
            {ticketData.pesee.tenant && (
              <div className="flex justify-between">
                <span className="text-gray-600">Tenant:</span>
                <span className="font-medium">{ticketData.pesee.tenant}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Date pesée brut:</span>
              <span className="font-medium">{formatDateTime(ticketData.pesee.grossAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date pesée tare:</span>
              <span className="font-medium">{formatDateTime(ticketData.pesee.tareAt)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Analyse qualité (si disponible) */}
      {ticketData.pesee.analyse_qualite && (
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-300 pb-1">Analyse qualité</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ticketData.pesee.analyse_qualite.grainnage && (
              <div className="flex justify-between">
                <span className="text-gray-600">Grainnage:</span>
                <span className="font-medium">{ticketData.pesee.analyse_qualite.grainnage}</span>
              </div>
            )}
            {ticketData.pesee.analyse_qualite.humidite !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-600">Taux d'humidité:</span>
                <span className="font-medium">{ticketData.pesee.analyse_qualite.humidite}%</span>
              </div>
            )}
            {ticketData.pesee.analyse_qualite.quality !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-600">Qualité:</span>
                <span className="font-medium">{ticketData.pesee.analyse_qualite.quality}</span>
              </div>
            )}
            {ticketData.pesee.analyse_qualite.quality_level && (
              <div className="flex justify-between">
                <span className="text-gray-600">Niveau de qualité:</span>
                <span className="font-medium">{ticketData.pesee.analyse_qualite.quality_level}</span>
              </div>
            )}
            {ticketData.pesee.analyse_qualite.quality_decision && (
              <div className="flex justify-between col-span-2">
                <span className="text-gray-600">Décision qualité:</span>
                <span className="font-medium">{ticketData.pesee.analyse_qualite.quality_decision}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Motifs de refoulement */}
      <div className="mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-300 pb-1">Motifs de refoulement</h2>
        <div className="space-y-2">
          {ticketData.motifs && ticketData.motifs.length > 0 ? (
            ticketData.motifs.map((motif: any, index: number) => {
              // Normaliser les données du motif pour l'affichage
              // Prioriser libelle, puis nom, puis name, puis id
              // Remplacer les underscores par des espaces
              const cleanLibelle = (str: string) => str ? str.replace(/_/g, ' ') : '';
              const motifLibelleRaw = motif?.libelle || motif?.nom || motif?.name || (typeof motif === 'string' ? motif : String(motif?.id || ''));
              const motifLibelle = cleanLibelle(motifLibelleRaw);
              
              return (
                <div key={index} className="flex items-start">
                  <span className="text-red-600 font-bold mr-2">*</span>
                  <span className="text-gray-900">{motifLibelle}</span>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-gray-600">Aucun motif spécifié</p>
          )}
        </div>
      </div>

      {/* Observations */}
      {ticketData.observations && (
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3 border-b border-gray-300 pb-1">Observations</h2>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticketData.observations}</p>
          </div>
        </div>
      )}

      {/* Émargement */}
      <div className="mt-8 pt-6 border-t-2 border-gray-300">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="text-center">
            <div className="border-t-2 border-gray-400 pt-2 w-32 mx-auto mb-2"></div>
            <p className="font-bold text-gray-900">AGENT REFOULEMENT</p>
            <p className="text-sm text-gray-600 mt-1">
              {ticketData.agent_refoulement_nom || ticketData.agent_refoulement || ''}
            </p>
          </div>
          <div className="text-center">
            <div className="border-t-2 border-gray-400 pt-2 w-32 mx-auto mb-2"></div>
            <p className="font-bold text-gray-900">VISA CLIENT / FOURNISSEUR</p>
            <p className="text-sm text-gray-600 mt-1"></p>
          </div>
        </div>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="border-t border-gray-200 pt-6">
          <h3 className="font-medium text-gray-900 mb-4">Actions disponibles</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className="flex flex-col items-center p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PrinterIcon className="w-5 h-5 mb-1" />
              <span className="text-xs">{isPrinting ? 'Impression...' : 'Imprimer'}</span>
            </button>

            <button
              onClick={handleExport}
              disabled={isExporting}
              className="flex flex-col items-center p-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mb-1" />
              <span className="text-xs">{isExporting ? 'Export...' : 'Exporter PDF'}</span>
            </button>

            <button
              onClick={handleEmail}
              disabled={isSendingEmail}
              className="flex flex-col items-center p-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <EnvelopeIcon className="w-5 h-5 mb-1" />
              <span className="text-xs">{isSendingEmail ? 'Envoi...' : 'Envoyer Email'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Pied de page */}
      <div className="mt-6 pt-4 border-t border-gray-200 text-center text-xs text-gray-500">
        <p>Généré le {ticketData.generated_at ? formatDateTime(ticketData.generated_at) : formatDateTime(new Date().toISOString())}</p>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
          
          <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            {onClose && (
              <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                    <XMarkIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Ticket de Refoulement
                    </h3>
                    <p className="text-sm text-gray-600">
                      N° {ticketData.numero_refoulement}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            )}

            {/* Ticket Content */}
            <div className="p-6">
              {ticketContent}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return ticketContent;
};

export default RefoulementTicket;
